'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';

interface Section {
  id: string;
  title: string;
  subsections?: { id: string; title: string }[];
}

interface SpecificationData {
  label: string;
  value: string;
}

const RopedHydraulicElevatorSpecifications: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('part1');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['part1']));

  const sections: Section[] = [
    {
      id: 'part1',
      title: 'Part 1: General',
      subsections: [
        { id: '1.1', title: '1.1 Description of Work' },
        { id: '1.2', title: '1.2 Work By Others' },
        { id: '1.3', title: '1.3 Quality Assurance' }
      ]
    },
    {
      id: 'part2',
      title: 'Part 2: Submittals',
      subsections: [
        { id: '2.1', title: '2.1 Product Data' },
        { id: '2.2', title: '2.2 Layout Drawings' }
      ]
    },
    {
      id: 'part3',
      title: 'Part 3: Product',
      subsections: [
        { id: '3.1', title: '3.1 Manufacturer' },
        { id: '3.2', title: '3.2 Characteristics' },
        { id: '3.3', title: '3.3 Equipment' }
      ]
    },
    {
      id: 'part4',
      title: 'Part 4: Execution',
      subsections: [
        { id: '4.1', title: '4.1 Examination' },
        { id: '4.2', title: '4.2 Installation' },
        { id: '4.3', title: '4.3 Operating Instructions' },
        { id: '4.4', title: '4.4 Maintenance' },
        { id: '4.5', title: '4.5 Warranty' }
      ]
    }
  ];

  const characteristics: SpecificationData[] = [
    { label: 'Type', value: 'Roped 2:1 Hydraulic' },
    { label: 'Capacity', value: '950 lbs' },
    { label: 'Car Speed', value: '40 FPM' },
    { label: 'Operation', value: 'SAPB/Single Button Collective' },
    { label: 'Inside Car Dimensions', value: '36" × 48" × 80" high' },
    { label: 'Power Supply', value: '220 Volt, Single Phase, 60 Hz' }
  ];

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center space-x-2 text-sm mb-8">
            <Link href="/resources" className="text-gray-500 hover:text-black transition-colors">
              Resources
            </Link>
            <span className="text-gray-300">/</span>
            <span className="text-black font-medium">Technical Specifications</span>
          </nav>
          
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6">
              Roped Hydraulic Residential
              <br />
              <span className="font-medium">Elevator Specifications</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
              Comprehensive technical documentation for residential elevator systems,
              including installation requirements, equipment specifications, and compliance standards.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          
          {/* Sticky Navigation */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-8">
              {/* Search */}
              <div>
                <input
                  type="text"
                  placeholder="Search specifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 focus:border-black focus:outline-none transition-colors"
                />
              </div>

              {/* Navigation Menu */}
              <nav className="space-y-1">
                {sections.map((section) => (
                  <div key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-4 py-2 transition-colors ${
                        activeSection === section.id
                          ? 'bg-black text-white' :'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span className="text-sm font-medium">{section.title}</span>
                    </button>
                    {section.subsections && expandedSections.has(section.id) && (
                      <div className="ml-4 space-y-1">
                        {section.subsections.map((subsection) => (
                          <button
                            key={subsection.id}
                            onClick={() => scrollToSection(subsection.id)}
                            className="w-full text-left px-4 py-1.5 text-xs text-gray-600 hover:text-black transition-colors"
                          >
                            {subsection.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>

              {/* Quick Actions */}
              <div className="pt-6 border-t border-gray-200 space-y-3">
                <button className="w-full px-4 py-2 bg-black text-white text-sm hover:bg-gray-900 transition-colors">
                  Download PDF
                </button>
                <button className="w-full px-4 py-2 border border-gray-300 text-sm hover:border-black transition-colors">
                  Print Version
                </button>
              </div>
            </div>
          </aside>

          {/* Content Area */}
          <main className="lg:col-span-3 space-y-16">
            
            {/* Part 1: General */}
            <section id="part1" className="scroll-mt-24">
              <h2 className="text-3xl font-light mb-8 pb-4 border-b border-gray-200">
                Part 1: General
              </h2>

              <div id="1.1" className="mb-12 scroll-mt-24">
                <h3 className="text-xl font-medium mb-4">1.1 Description of Work</h3>
                <p className="text-gray-700 leading-relaxed">
                  To furnish all labor and materials required to cover a complete installation of one (1) roped 
                  hydraulic residential elevator. The elevator is to be installed in a first class workmanlike 
                  manner in accordance with the specifications and drawings provided.
                </p>
              </div>

              <div id="1.2" className="mb-12 scroll-mt-24">
                <h3 className="text-xl font-medium mb-6">1.2 Work By Others</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The following preparatory work to accommodate the elevator installation is to be done by 
                  others and is part of work of other sections.
                </p>

                {/* Hoistway Requirements */}
                <div className="mb-8">
                  <h4 className="font-medium mb-4 flex items-center">
                    <span className="w-2 h-2 bg-black rounded-full mr-3"></span>
                    A. Hoistway
                  </h4>
                  <ul className="space-y-3 ml-5 text-gray-700">
                    <li className="pl-4 border-l-2 border-gray-200">
                      A finished, plumb hoistway of proper size and construction conforming to ASME A17.1, 
                      all applicable building codes, and the elevator layout drawings.
                    </li>
                    <li className="pl-4 border-l-2 border-gray-200">
                      Adequate supports shall be provided for fastening rail brackets as indicated on the 
                      layout drawings. Supports must withstand rail forces indicated.
                    </li>
                    <li className="pl-4 border-l-2 border-gray-200">
                      A poured pit conforming to all applicable codes, ASME A17.1, and to the dimensions 
                      indicated on the layout drawings must be provided. The pit must be designed for the 
                      impact load indicated and must be guaranteed dry and level from wall to wall.
                    </li>
                    <li className="pl-4 border-l-2 border-gray-200">
                      Knock-out in walls between the machine room and elevator hoistway for routing hydraulic 
                      and electrical lines and for hall buttons shall be coordinated with the elevator contractor.
                    </li>
                    <li className="pl-4 border-l-2 border-gray-200">
                      All wall patching, painting, and grouting by others.
                    </li>
                    <li className="pl-4 border-l-2 border-gray-200">
                      Hoistway doors, frames, entrances, sills, and associated framing to be provided and 
                      installed by purchaser or general contractor.
                    </li>
                  </ul>
                </div>

                {/* Machine Room Requirements */}
                <div className="mb-8">
                  <h4 className="font-medium mb-4 flex items-center">
                    <span className="w-2 h-2 bg-black rounded-full mr-3"></span>
                    B. Machine Room
                  </h4>
                  <ul className="space-y-3 ml-5 text-gray-700">
                    <li className="pl-4 border-l-2 border-gray-200">
                      An adjacent machine room built to conform to the layout drawings, NFPA 70, ASME A17.1, 
                      and all applicable building code requirements. It shall have suitable access, a lockable 
                      door, a convenience outlet, and light switch. Machine room temperature must be maintained 
                      between 60 and 100 degrees Fahrenheit. Relative humidity not to exceed 95%.
                    </li>
                    <li className="pl-4 border-l-2 border-gray-200">
                      A telephone line to the machine room and tied into the elevator controller as per 
                      ANSI/ASME A17.1 code.
                    </li>
                    <li className="pl-4 border-l-2 border-gray-200">
                      Machine room vents as required by local code.
                    </li>
                  </ul>
                </div>

                {/* Electrical Requirements */}
                <div className="mb-8">
                  <h4 className="font-medium mb-4 flex items-center">
                    <span className="w-2 h-2 bg-black rounded-full mr-3"></span>
                    C. Electrical Requirements
                  </h4>
                  <ul className="space-y-3 ml-5 text-gray-700">
                    <li className="pl-4 border-l-2 border-gray-200">
                      A 220VAC, single phase service, with neutral, to a lockable safety disconnect switch, 
                      fused with time delay fuses shall be furnished in the machine room in accordance with 
                      NFPA 70. A normally open electric interlock contact is required in the switch for battery isolation.
                    </li>
                    <li className="pl-4 border-l-2 border-gray-200">
                      A 120VAC, single phase, 15 AMP service to a lockable fused disconnect switch, or circuit 
                      breaker, located in the machine room shall be provided for the cab lighting in accordance 
                      with NFPA 70.
                    </li>
                  </ul>
                </div>
              </div>

              <div id="1.3" className="mb-12 scroll-mt-24">
                <h3 className="text-xl font-medium mb-6">1.3 Quality Assurance</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The elevator shall be designed, manufactured, installed, and inspected in accordance with 
                  ANSI/ASME A17.1 standards and all applicable regulations of federal, state, and local codes 
                  and ordinances as adopted by local agencies having jurisdiction.
                </p>

                <div className="bg-gray-50 p-6 space-y-6">
                  <div>
                    <h4 className="font-medium mb-3">A. References</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• American National Standards Institute (ANSI)</li>
                      <li>• American Society of Mechanical Engineers (ASME)</li>
                      <li>• National Electric Code (NFPA 70)</li>
                      <li>• CSA B44.1/ASME A17.5, elevator and escalator electrical equipment requirements</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3">B. Qualifications</h4>
                    <p className="text-gray-700 text-sm">
                      The installation shall be performed by a company with no less than five (5) years of 
                      successful experience in the assembly and erection of similar type elevators and who has 
                      adequate product liability insurance.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3">C. Regulatory Requirements</h4>
                    <p className="text-gray-700 text-sm">
                      The elevator installer shall verify requirements of the local authority having jurisdiction 
                      and shall obtain and pay for necessary municipal and state permits and inspections as 
                      required, and make tests as called for by the regulations of such authorities.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Part 2: Submittals */}
            <section id="part2" className="scroll-mt-24">
              <h2 className="text-3xl font-light mb-8 pb-4 border-b border-gray-200">
                Part 2: Submittals
              </h2>

              <div id="2.1" className="mb-8 scroll-mt-24">
                <h3 className="text-xl font-medium mb-4">2.1 Product Data</h3>
                <p className="text-gray-700 leading-relaxed">
                  Submit manufacturers literature including product data, cab designs, color charts, signal 
                  fixtures, and specifications.
                </p>
              </div>

              <div id="2.2" className="mb-8 scroll-mt-24">
                <h3 className="text-xl font-medium mb-4">2.2 Layout Drawings</h3>
                <p className="text-gray-700 leading-relaxed">
                  Layout drawings shall be submitted showing the general arrangement of the elevator equipment 
                  including dimensions, clearances, location of machine equipment, and all loads and reactions 
                  imposed on pit and building structure.
                </p>
              </div>
            </section>

            {/* Part 3: Product */}
            <section id="part3" className="scroll-mt-24">
              <h2 className="text-3xl font-light mb-8 pb-4 border-b border-gray-200">
                Part 3: Product
              </h2>

              <div id="3.1" className="mb-12 scroll-mt-24">
                <h3 className="text-xl font-medium mb-4">3.1 Manufacturer</h3>
                <div className="bg-gray-50 p-6">
                  <p className="text-gray-700 leading-relaxed">
                    The roped hydraulic residential elevator shall be manufactured by Commercial and Residential 
                    Elevator Supply.
                  </p>
                  <div className="mt-4 text-sm text-gray-600">
                    <p>Kennesaw, GA 30144</p>
                    <p>Phone: 770-852-5050</p>
                  </div>
                </div>
              </div>

              <div id="3.2" className="mb-12 scroll-mt-24">
                <h3 className="text-xl font-medium mb-6">3.2 Characteristics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {characteristics.map((spec, index) => (
                    <div key={index} className="border border-gray-200 p-4 hover:border-black transition-colors">
                      <div className="text-xs text-gray-500 mb-1">{spec.label}</div>
                      <div className="font-medium">{spec.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div id="3.3" className="mb-12 scroll-mt-24">
                <h3 className="text-xl font-medium mb-6">3.3 Equipment</h3>

                {/* Operation Section */}
                <div className="mb-10">
                  <h4 className="font-medium mb-4 text-lg">A. Operation</h4>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Operation of the elevator shall be single automatic push button or single button collective 
                    (field programmable); momentary pressure on any button will call or send the elevator to the 
                    corresponding landing providing all doors are closed.
                  </p>

                  <div className="space-y-6">
                    {[
                      {
                        title: '1. Battery Lowering',
                        content: 'In the event of a power failure, the elevator shall automatically descend to the homepark landing, wait 30 seconds, then proceed to the bottom landing while monitoring all safety circuits. If the elevator is equipped with a power car door(s) option, the car door(s) shall open and close automatically. Batteries are to have an automatic charging system.'
                      },
                      {
                        title: '2. Emergency Car Lighting',
                        content: 'In the event of a power failure, the car lights shall automatically transfer to battery power.'
                      },
                      {
                        title: '3. Homepark Feature',
                        content: 'The elevator shall automatically return to a field programmable designated landing after one minute without use.'
                      },
                      {
                        title: '4. Automatic Two-Way Leveling',
                        content: 'The leveling device shall automatically stop and maintain the car within ½ inch of the landing regardless of the change in load.'
                      },
                      {
                        title: '5. Low Oil Control',
                        content: 'A low oil control feature shall be provided designed to automatically cause an up traveling car to descend to the lowest terminal landing if the elevator should fail to reach a landing in a predetermined time or if the system does not have a sufficient reservoir of oil.'
                      }
                    ].map((item, index) => (
                      <div key={index} className="pl-6 border-l-2 border-gray-200">
                        <h5 className="font-medium mb-2">{item.title}</h5>
                        <p className="text-gray-700 text-sm leading-relaxed">{item.content}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Control System */}
                <div className="mb-10">
                  <h4 className="font-medium mb-4 text-lg">B. Control System</h4>
                  <p className="text-gray-700 leading-relaxed">
                    A microprocessor based control system certified and labeled to the requirements of 
                    CAN/CSAB44.1/ASME A17.5 shall be provided. It shall include a motor starter with a potential 
                    relay, motor overload device, an uninterrupted power supply with battery charging circuit, 
                    and external relay redundancy circuits to prevent dangerous conditions as a result of a single 
                    contact failure. All circuits shall be fuse protected. All to be enclosed in a single NEMA 1 
                    lockable cabinet.
                  </p>
                </div>

                {/* Hydraulic Power Unit */}
                <div className="mb-10">
                  <h4 className="font-medium mb-4 text-lg">C. Hydraulic Power Unit</h4>
                  <p className="text-gray-700 leading-relaxed">
                    The hydraulic power unit shall include a submersible motor, rotary screw type pump, two-speed 
                    control valve, and oil reservoir with an oil level gauge. The control valve shall include a 
                    safety check valve, up and down acceleration, deceleration, leveling, and soft stop adjustments, 
                    pressure relief valve, manual lowering valve, constant down speed regulation, pressure gauge 
                    with shutoff, negative pressure switch, and manual shutoff valve all mounted and enclosed in a 
                    compact unit assembly with a key lockable cover.
                  </p>
                </div>

                {/* Additional Equipment Sections */}
                {[
                  {
                    id: 'D',
                    title: 'D. Plunger and Cylinder',
                    content: 'The cylinder shall be constructed of steel pipe with a steel bulkplate welded to the lower end and a cylinder head welded on the upper end which houses the self-adjusting packing, bearings, wiper, air bleeder, and leach line hose. The plunger shall be manufactured from accurately ground and polished tubing fitted with a steel stop ring welded to the bottom to prevent the plunger from leaving the cylinder in the up direction.'
                  },
                  {
                    id: 'E',
                    title: 'E. Pipe Rupture Valve',
                    content: 'An automatic shut off valve at the cylinder inlet shall be provided to stop and hold the elevator in the event of a main oil line failure or if the elevator should overspeed in the down direction.'
                  },
                  {
                    id: 'F',
                    title: 'F. Car Frame and Platform',
                    content: 'The car frame shall be fabricated from structural and formed steel members, welded and bolted construction, of the cantilevered design. It shall be fitted with roller guide shoes, broken rope car safeties, and a slack cable switch that will disconnect power to the control valve if a rope should be become slack or broken. The car platform shall be fabricated from 1-1/4" plywood protected with a fire retardant material.'
                  },
                  {
                    id: 'G',
                    title: 'G. Car Suspension',
                    content: 'The elevator car frame shall be suspended by (2) 3/8" diameter, 6 × 19, traction steel cables. The cables shall dead end to the pit steel on one end, pass over a "U" groove sheave, and attach to the car safety device with approved type wedge sockets.'
                  },
                  {
                    id: 'H',
                    title: 'H. Guide Rails',
                    content: 'The car guide rails shall consist of (2) machined steel "tee" sections, no less than 8 lb. per foot, securely fastened to the hoistway structure with steel brackets. All rail end sections shall be tongue & groove type joined with steel splice plates.'
                  },
                  {
                    id: 'I',
                    title: 'I. Car Operating Panel',
                    content: 'Car operating panel shall consist of metal lens call push buttons with red LED halo lighting for each landing, an alarm button, emergency stop button, light switch, and a digital car position indicator with car direction arrows all mounted onto a brushed stainless steel faceplate (brass optional). Digital C.P.I. shall be field programmable.'
                  },
                  {
                    id: 'J',
                    title: 'J. Landing Controls',
                    content: 'Landing control stations shall consist of a metal lens call button and a "car here" indicator with red LED halo lighting mounted onto a brushed stainless steel faceplate (brass optional).'
                  },
                  {
                    id: 'K',
                    title: 'K. Hoistway Doors',
                    content: 'The general contractor or owner is to furnish (elevator contractor may opt to furnish) and install hoistway doors, frames, hinges, and passage sets at each landing. The type and installation of the doors and frames must comply with ASME A17.1, all state and local codes and as per manufacturer\'s layout drawings.'
                  },
                  {
                    id: 'L',
                    title: 'L. Car Door(s)',
                    content: 'The car door(s) shall be solid panel construction accordion type folding door(s) that prevent a person\'s hands or feet from extending through openings. Finish shall be vinyl laminate chosen from the manufacturer\'s standard color selections (hardwood, visifold, and alumifold optional). Car door shall be equipped with a positive contact switch to prevent elevator operation with the car door(s) open (power car door(s) optional).'
                  },
                  {
                    id: 'M',
                    title: 'M. Car Enclosure',
                    content: 'The cab walls shall be constructed of ¾" minimum plywood substrates faced with plastic laminate or wood veneers with wood trim accents as selected from the manufacturer\'s standard designs. A brushed stainless steel handrail (brass optional) shall be located on one wall. A telephone shall be furnished in the elevator cab for emergency communication. Cab ceiling shall be a minimum of ¾" thick substrate with at least a (2) bulb light fixture as selected from the manufacturer\'s standard ceiling designs. Finished flooring covering is to be furnished by others.'
                  },
                  {
                    id: 'N',
                    title: 'N. Electrical Wiring',
                    content: 'All wiring and electrical materials shall conform to NFPA 70 and with all applicable codes. Insulated wiring shall have flame-retardant and moisture proof outer covering and shall be run in conduit or electrical wireways as required. Traveling cables shall be flexible and suitably suspended to relieve strain.'
                  }
                ].map((section) => (
                  <div key={section.id} className="mb-8">
                    <h4 className="font-medium mb-4 text-lg">{section.title}</h4>
                    <p className="text-gray-700 leading-relaxed">{section.content}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Part 4: Execution */}
            <section id="part4" className="scroll-mt-24">
              <h2 className="text-3xl font-light mb-8 pb-4 border-b border-gray-200">
                Part 4: Execution
              </h2>

              <div className="space-y-10">
                <div id="4.1" className="scroll-mt-24">
                  <h3 className="text-xl font-medium mb-4">4.1 Examination</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Elevator installer shall verify dimensions of hoistway, pit, machine room, and inspect 
                    conditions of supports and structure prior to installation.
                  </p>
                </div>

                <div id="4.2" className="scroll-mt-24">
                  <h3 className="text-xl font-medium mb-4">4.2 Installation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The elevator shall be installed in accordance with the manufacturer's instructions and shall 
                    conform to ASME A17.1 and all state and local code requirements.
                  </p>
                </div>

                <div id="4.3" className="scroll-mt-24">
                  <h3 className="text-xl font-medium mb-4">4.3 Operating Instructions</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Upon completion of the installation, the owner shall be instructed on the elevator's operation, 
                    safety precautions, and maintenance requirements. The owner shall be supplied with an owner's 
                    manual to retain for reference.
                  </p>
                </div>

                <div id="4.4" className="scroll-mt-24">
                  <h3 className="text-xl font-medium mb-4">4.4 Maintenance</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The elevator shall be maintained in accordance with the manufacturer's recommendations and all 
                    applicable codes.
                  </p>
                </div>

                <div id="4.5" className="scroll-mt-24">
                  <h3 className="text-xl font-medium mb-4">4.5 Warranty</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The elevator shall have a two (2) year limited parts warranty.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact CTA */}
            <section className="mt-20 pt-12 border-t border-gray-200">
              <div className="bg-black text-white p-12 text-center">
                <h3 className="text-2xl font-light mb-4">Need Technical Support?</h3>
                <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                  Our engineering team is available to answer questions about specifications,
                  installation requirements, and compliance standards.
                </p>
                <Link
                  href="/contact"
                  className="inline-block px-8 py-3 bg-white text-black font-medium hover:bg-gray-100 transition-colors"
                >
                  Contact Engineering Team
                </Link>
              </div>
            </section>

          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RopedHydraulicElevatorSpecifications;